interface PerformanceMetric {
  name: string
  value: number
  timestamp: number
  context?: Record<string, unknown>
}

interface WebVitalsMetric {
  name: 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB'
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  timestamp: number
}

interface ComponentPerformance {
  componentName: string
  renderTime: number
  timestamp: number
  props?: Record<string, unknown>
}

export class PerformanceService {
  private metrics: PerformanceMetric[] = []
  private webVitals: WebVitalsMetric[] = []
  private componentMetrics: ComponentPerformance[] = []
  private readonly maxMetrics = 1000
  
  constructor() {
    this.initializeWebVitals()
    this.initializeNavigationTiming()
  }

  /**
   * Initialize Web Vitals monitoring
   */
  private initializeWebVitals(): void {
    if (typeof window === 'undefined') {return}

    // Dynamic import for web-vitals (if we add it as dependency)
    // For now, we'll use PerformanceObserver API directly
    this.observeLCP()
    this.observeFID()
    this.observeCLS()
    this.observeFCP()
    this.observeTTFB()
  }

  /**
   * Observe Largest Contentful Paint
   */
  private observeLCP(): void {
    if (!('PerformanceObserver' in window)) {return}

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        const lastEntry = entries[entries.length - 1]
        const lcpEntry = lastEntry as PerformanceEntry & { startTime: number }
        
        if (lastEntry && lcpEntry.startTime) {
          this.recordWebVital('LCP', lcpEntry.startTime)
        }
      })
      
      observer.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch (error) {
      console.warn('LCP observation failed:', error)
    }
  }

  /**
   * Observe First Input Delay
   */
  private observeFID(): void {
    if (!('PerformanceObserver' in window)) {return}

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        
        entries.forEach((entry) => {
          const fidEntry = entry as PerformanceEntry & { processingStart?: number; startTime: number }
          if (fidEntry.processingStart && fidEntry.startTime) {
            const fid = fidEntry.processingStart - fidEntry.startTime
            this.recordWebVital('FID', fid)
          }
        })
      })
      
      observer.observe({ type: 'first-input', buffered: true })
    } catch (error) {
      console.warn('FID observation failed:', error)
    }
  }

  /**
   * Observe Cumulative Layout Shift
   */
  private observeCLS(): void {
    if (!('PerformanceObserver' in window)) {return}

    try {
      let clsValue = 0
      
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        
        entries.forEach((entry) => {
          const layoutShiftEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value: number }
          if (!layoutShiftEntry.hadRecentInput && layoutShiftEntry.value) {
            clsValue += layoutShiftEntry.value
          }
        })
        
        this.recordWebVital('CLS', clsValue)
      })
      
      observer.observe({ type: 'layout-shift', buffered: true })
    } catch (error) {
      console.warn('CLS observation failed:', error)
    }
  }

  /**
   * Observe First Contentful Paint
   */
  private observeFCP(): void {
    if (!('PerformanceObserver' in window)) {return}

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        
        entries.forEach((entry) => {
          const paintEntry = entry as PerformanceEntry & { startTime: number }
          if (entry.name === 'first-contentful-paint' && paintEntry.startTime) {
            this.recordWebVital('FCP', paintEntry.startTime)
          }
        })
      })
      
      observer.observe({ type: 'paint', buffered: true })
    } catch (error) {
      console.warn('FCP observation failed:', error)
    }
  }

  /**
   * Observe Time to First Byte
   */
  private observeTTFB(): void {
    if (typeof window === 'undefined' || !window.performance?.timing) {return}

    try {
      const timing = window.performance.timing
      const ttfb = timing.responseStart - timing.requestStart
      this.recordWebVital('TTFB', ttfb)
    } catch (error) {
      console.warn('TTFB observation failed:', error)
    }
  }

  /**
   * Record a Web Vital metric
   */
  private recordWebVital(name: WebVitalsMetric['name'], value: number): void {
    const rating = this.getWebVitalRating(name, value)
    
    const metric: WebVitalsMetric = {
      name,
      value,
      rating,
      timestamp: Date.now()
    }
    
    this.webVitals.push(metric)
    this.trimMetrics()
    
    // Log significant performance issues
    if (rating === 'poor') {
      console.warn(`Poor ${name} performance detected:`, value)
    }
  }

  /**
   * Get Web Vital rating based on thresholds
   */
  private getWebVitalRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds = {
      LCP: { good: 2500, needsImprovement: 4000 },
      FID: { good: 100, needsImprovement: 300 },
      CLS: { good: 0.1, needsImprovement: 0.25 },
      FCP: { good: 1800, needsImprovement: 3000 },
      TTFB: { good: 800, needsImprovement: 1800 }
    }

    const threshold = thresholds[name as keyof typeof thresholds]
    if (!threshold) {return 'good'}

    if (value <= threshold.good) {return 'good'}
    if (value <= threshold.needsImprovement) {return 'needs-improvement'}
    return 'poor'
  }

  /**
   * Initialize Navigation Timing monitoring
   */
  private initializeNavigationTiming(): void {
    if (typeof window === 'undefined') {return}

    window.addEventListener('load', () => {
      setTimeout(() => {
        this.recordNavigationTimings()
      }, 0)
    })
  }

  /**
   * Record navigation timing metrics
   */
  private recordNavigationTimings(): void {
    if (!window.performance?.timing) {return}

    const timing = window.performance.timing
    const metrics = [
      { name: 'domainLookup', value: timing.domainLookupEnd - timing.domainLookupStart },
      { name: 'tcpConnect', value: timing.connectEnd - timing.connectStart },
      { name: 'request', value: timing.responseEnd - timing.requestStart },
      { name: 'domLoad', value: timing.domContentLoadedEventEnd - timing.navigationStart },
      { name: 'pageLoad', value: timing.loadEventEnd - timing.navigationStart }
    ]

    metrics.forEach(metric => {
      if (metric.value > 0) {
        this.recordMetric(metric.name, metric.value, { type: 'navigation' })
      }
    })
  }

  /**
   * Record a performance metric
   */
  public recordMetric(name: string, value: number, context?: Record<string, unknown>): void {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      context
    }
    
    this.metrics.push(metric)
    this.trimMetrics()
  }

  /**
   * Record component render performance
   */
  public recordComponentPerformance(
    componentName: string, 
    renderTime: number, 
    props?: Record<string, unknown>
  ): void {
    const metric: ComponentPerformance = {
      componentName,
      renderTime,
      timestamp: Date.now(),
      props
    }
    
    this.componentMetrics.push(metric)
    this.trimMetrics()

    // Warn about slow component renders
    if (renderTime > 16) { // > 1 frame at 60fps
      console.warn(`Slow render detected for ${componentName}: ${renderTime}ms`)
    }
  }

  /**
   * Create a performance timer
   */
  public createTimer(label: string): () => void {
    const startTime = performance.now()
    
    return () => {
      const endTime = performance.now()
      const duration = endTime - startTime
      this.recordMetric(`timer_${label}`, duration, { type: 'timer' })
    }
  }

  /**
   * Create a component render timer
   */
  public createComponentTimer(componentName: string, props?: Record<string, unknown>): () => void {
    const startTime = performance.now()
    
    return () => {
      const endTime = performance.now()
      const duration = endTime - startTime
      this.recordComponentPerformance(componentName, duration, props)
    }
  }

  /**
   * Get performance summary
   */
  public getPerformanceSummary(): {
    webVitals: Record<string, { latest: number; average: number; rating: string }>
    slowComponents: Array<{ name: string; averageTime: number; count: number }>
    navigationMetrics: Record<string, number>
  } {
    // Web Vitals summary
    const webVitalsSummary: Record<string, { latest: number; average: number; rating: string }> = {}
    
    const vitalNames = ['LCP', 'FID', 'CLS', 'FCP', 'TTFB'] as const
    vitalNames.forEach(name => {
      const vitals = this.webVitals.filter(v => v.name === name)
      if (vitals.length > 0) {
        const latest = vitals[vitals.length - 1]
        const average = vitals.reduce((sum, v) => sum + v.value, 0) / vitals.length
        
        webVitalsSummary[name] = {
          latest: latest.value,
          average,
          rating: latest.rating
        }
      }
    })

    // Slow components analysis
    const componentGroups = new Map<string, number[]>()
    this.componentMetrics.forEach(metric => {
      if (!componentGroups.has(metric.componentName)) {
        componentGroups.set(metric.componentName, [])
      }
      componentGroups.get(metric.componentName)!.push(metric.renderTime)
    })

    const slowComponents = Array.from(componentGroups.entries())
      .map(([name, times]) => ({
        name,
        averageTime: times.reduce((sum, time) => sum + time, 0) / times.length,
        count: times.length
      }))
      .filter(comp => comp.averageTime > 10) // > 10ms average
      .sort((a, b) => b.averageTime - a.averageTime)
      .slice(0, 10)

    // Navigation metrics
    const navigationMetrics: Record<string, number> = {}
    this.metrics
      .filter(m => m.context?.type === 'navigation')
      .forEach(metric => {
        navigationMetrics[metric.name] = metric.value
      })

    return {
      webVitals: webVitalsSummary,
      slowComponents,
      navigationMetrics
    }
  }

  /**
   * Get recent metrics
   */
  public getRecentMetrics(timeWindow: number = 5 * 60 * 1000): PerformanceMetric[] {
    const cutoff = Date.now() - timeWindow
    return this.metrics.filter(m => m.timestamp > cutoff)
  }

  /**
   * Clear all metrics
   */
  public clearMetrics(): void {
    this.metrics = []
    this.webVitals = []
    this.componentMetrics = []
  }

  /**
   * Trim metrics to prevent memory leaks
   */
  private trimMetrics(): void {
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics)
    }
    if (this.webVitals.length > this.maxMetrics) {
      this.webVitals = this.webVitals.slice(-this.maxMetrics)
    }
    if (this.componentMetrics.length > this.maxMetrics) {
      this.componentMetrics = this.componentMetrics.slice(-this.maxMetrics)
    }
  }

  /**
   * Export metrics for analysis
   */
  public exportMetrics(): {
    timestamp: number
    metrics: PerformanceMetric[]
    webVitals: WebVitalsMetric[]
    componentMetrics: ComponentPerformance[]
    summary: ReturnType<PerformanceService['getPerformanceSummary']>
  } {
    return {
      timestamp: Date.now(),
      metrics: [...this.metrics],
      webVitals: [...this.webVitals],
      componentMetrics: [...this.componentMetrics],
      summary: this.getPerformanceSummary()
    }
  }
} 